/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useEffect, useRef, useState } from "react";
import { theme } from "styles/theme";
import Icon from "uis/Icon";

const InputField = styled.input`
  border: none;
  width: 20px;
  text-align: center;
  font-family: Pretendard;
  font-size: 15px;
  background-color: transparent;

  &:focus {
    outline: none;
    background-color: ${theme.primary100};
  }
`;

const SelectContainer = styled.div`
  background-color: white;
  border-radius: 12px;
  filter: drop-shadow(0px 4px 20px rgba(69, 76, 83, 0.15));
  padding: 4px;

  position: absolute;
  display: flex;
  top: 50px;
  left: 0;
  width: max-content;
  max-height: 200px;
  z-index: 1;
  cursor: pointer;
`;

const SelectItem = styled.p<{ selected: boolean }>`
  padding: 12px;
  border-radius: 8px;
  background-color: ${(props) => (props.selected ? theme.neutral50 : "white")};
  font-weight: ${(props) => (props.selected ? 700 : 500)};
  color: ${(props) => (props.selected ? theme.primary500 : theme.neutral800)};
  &:hover {
    background-color: ${theme.neutral50};
  }
  font-size: 13px;
  margin: 4px;
`;

export default function TimeSelect({
  date,
  onChange,
}: {
  date: Date;
  onChange?: (hour: number, minutes: number) => void;
}) {
  const [hour, setHour] = useState<number>(date.getHours());
  const [minutes, setMinutes] = useState<number>(date.getMinutes());
  const [open, setOpen] = useState<boolean>(false);
  const hour_items = Array.from({ length: 24 }, (_, index) => index);
  const minute_items = Array.from({ length: 4 }, (_, index) => index * 15);
  const hourItemRefs = useRef<HTMLDivElement>(null);
  const minutesItemRefs = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && hourItemRefs.current) {
      const selectedHourRef = hourItemRefs.current.children[hour];
      selectedHourRef.scrollIntoView({ block: "nearest" });
    }
  }, [hour, open]);

  useEffect(() => {
    if (open && minutesItemRefs.current) {
      const selectedHourRef =
        minutesItemRefs.current.children[Math.floor(minutes / 15)];
      selectedHourRef.scrollIntoView({ block: "nearest" });
    }
  }, [minutes, open]);

  useEffect(() => {
    setHour(date.getHours());
    setMinutes(date.getMinutes());
  }, [date]);

  return (
    <button
      onClick={(e) => {
        const clickedElement = e.target as HTMLElement;
        if (clickedElement.tagName.toLowerCase() === "input") {
          setOpen(false);
        } else {
          setOpen(true);
        }
      }}
      onBlur={() => setOpen(false)}
      css={css`
        padding: 0;
        border: 0;
        background-color: transparent;
        align-items: center;
        display: flex;
        font-family: Pretendard;
      `}
    >
      <div
        css={css`
          background-color: ${theme.neutral50};
          padding: 8px 16px;
          border-radius: 10px;
          position: relative;
          display: flex;
          align-items: center;
          width: 100%;

          &:hover {
            background-color: ${theme.neutral100};
          }
        `}
      >
        <InputField
          onFocus={() => setOpen(false)}
          type="text"
          list="hourNumbers"
          value={hour?.toString().padStart(2, "0")}
          max={23}
          min={0}
          onChange={(e) => {
            let selected = hour;
            const regex = /^[0-9]*$/;
            if (regex.test(e.target.value)) {
              const value = parseInt(e.target.value) % 100;
              if (value > 23) {
                selected = value % 20;
              } else selected = value;
              setHour(selected);
              onChange?.(selected, minutes);
            }
          }}
        />
        <span>:</span>
        <InputField
          onFocus={() => setOpen(false)}
          css={css`
            margin-right: auto;
          `}
          type="text"
          value={minutes?.toString().padStart(2, "0")}
          max={60}
          min={0}
          onChange={(e) => {
            let selected = minutes;
            const regex = /^[0-9]*$/;
            if (regex.test(e.target.value)) {
              const value = parseInt(e.target.value) % 100;
              if (value > 60) {
                selected = value % 60;
              } else selected = value;
              setMinutes(selected);
              onChange?.(hour, selected);
            }
          }}
        />

        <Icon name="watch" width={28} color={theme.neutral400} />

        {open && (
          <SelectContainer>
            <div
              ref={hourItemRefs}
              css={css`
                height: 200px;
                overflow: auto;
                ::-webkit-scrollbar {
                  width: 0;
                  height: 0;
                }
                ::-webkit-scrollbar-track {
                  background: transparent;
                }
              `}
            >
              {hour_items.map((item, index) => (
                <SelectItem
                  selected={hour === item}
                  key={index}
                  onClick={(e) => {
                    setHour(item);
                    onChange?.(item, minutes);
                  }}
                >
                  {item.toString().padStart(2, "0")}
                </SelectItem>
              ))}
            </div>
            <div
              css={css`
                height: 200px;
                width: 2px;
                background-color: ${theme.neutral50};
              `}
            />
            <div
              ref={minutesItemRefs}
              css={css`
                height: 200px;
                overflow: auto;
                ::-webkit-scrollbar {
                  width: 0;
                  height: 0;
                }
                ::-webkit-scrollbar-track {
                  background: transparent;
                }
              `}
            >
              {minute_items.map((item, index) => (
                <SelectItem
                  selected={minutes === item}
                  key={index}
                  onClick={(e) => {
                    setMinutes(item);
                    onChange?.(hour, item);
                  }}
                >
                  {item.toString().padStart(2, "0")}
                </SelectItem>
              ))}
            </div>
          </SelectContainer>
        )}
      </div>
    </button>
  );
}
